import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, db } from '../services/firebase';
import PublicNavBar from './PublicNavBar';
import { useModal } from '../contexts/ModalContext';

const FeedbackPage = () => {
  const navigate = useNavigate();
  const [user] = useAuthState(auth);
  const { showAlert } = useModal();
  const [formData, setFormData] = useState({
    subject: '',
    teachingLevel: '',
    overallRating: '5',
    mostUsefulFeature: '',
    leastUsefulFeature: '',
    improvementSuggestion: '',
    additionalComments: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      await addDoc(collection(db, 'feedback'), {
        userId: user ? user.uid : 'anonymous',
        userEmail: user ? user.email : 'anonymous',
        subject: formData.subject,
        teachingLevel: formData.teachingLevel,
        overallRating: parseInt(formData.overallRating),
        mostUsefulFeature: formData.mostUsefulFeature,
        leastUsefulFeature: formData.leastUsefulFeature,
        improvementSuggestion: formData.improvementSuggestion,
        additionalComments: formData.additionalComments,
        timestamp: serverTimestamp(),
        status: 'new',
        userAgent: navigator.userAgent,
        url: window.location.href
      });
      
      setIsSubmitted(true);
      
    } catch (error) {
      console.error('Error submitting feedback:', error);
      await showAlert('Error submitting feedback. Please try again.', 'Error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sand-100 to-sand-200">
        <PublicNavBar />
        <div className="py-8 sm:py-12 md:py-16 px-2 sm:px-4">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-xl sm:rounded-2xl border-4 border-ink-900 p-4 sm:p-6 md:p-8 text-center">
              <div className="w-16 h-16 sm:w-18 sm:h-18 md:w-20 md:h-20 bg-coral-300 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <span className="text-white text-xl sm:text-2xl md:text-3xl">✅</span>
              </div>
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-ink-900 mb-3 sm:mb-4">THANK YOU!</h3>
              <p className="text-ink-600 text-sm sm:text-base md:text-lg mb-6">
                Your feedback has been submitted successfully. We really appreciate you taking the time to help us improve TeAIch!
              </p>
              <button 
                onClick={() => navigate('/')}
                className="bg-coral-300 text-white px-6 sm:px-8 py-2 sm:py-3 rounded-lg font-bold text-base sm:text-lg hover:bg-coral-500 transition duration-200"
              >
                Back to Home Page
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-sand-100 to-sand-200">
      
      <PublicNavBar />
      
      {/* Main Content */}
      <div className="py-8 sm:py-12 md:py-16 px-2 sm:px-4">
        <div className="max-w-4xl mx-auto">
          
          {/* Page Title */}
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-coral-500 mb-2 sm:mb-4 px-2 drop-shadow-[2px_2px_0_rgba(0,0,0,1)]">
              FEEDBACK
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-ink-900 opacity-80 px-2">
              Help us make TeAIch even better for educators
            </p>
          </div>

          {/* Intro Card */}
          <div className="mb-8 sm:mb-12 md:mb-16">
            <div className="bg-white rounded-xl sm:rounded-2xl border-4 border-ink-900 p-4 sm:p-6 md:p-8 text-center max-w-2xl mx-auto">
              <div className="w-16 h-16 sm:w-18 sm:h-18 md:w-20 md:h-20 bg-coral-300 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <span className="text-white text-xl sm:text-2xl md:text-3xl">💭</span>
              </div>
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-ink-900 mb-3 sm:mb-4">SHARE YOUR THOUGHTS</h3>
              <p className="text-ink-600 text-sm sm:text-base md:text-lg">
                Your feedback helps us understand what's working well and what we can improve. This form is anonymous - we'll use your insights to make TeAIch better for all educators.
              </p>
            </div>
          </div>

          {/* Feedback Form */}
          <div className="bg-white rounded-xl sm:rounded-2xl border-4 border-ink-900 p-4 sm:p-6 md:p-8 mb-8 sm:mb-12 md:mb-16">
            <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-ink-900 text-center mb-6 sm:mb-8">
              YOUR FEEDBACK
            </h3>
            
            <div className="max-w-2xl mx-auto space-y-4 sm:space-y-6">
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <label className="block text-ink-700 font-bold mb-2 text-sm sm:text-base">SUBJECT YOU TEACH</label>
                  <input 
                    type="text" 
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-sand-300 rounded-lg focus:outline-none focus:border-coral-500 text-sm sm:text-base" 
                    placeholder="e.g. Math, English, Science" 
                  />
                </div>
                <div>
                  <label className="block text-ink-700 font-bold mb-2 text-sm sm:text-base">LEVEL YOU TEACH</label>
                  <select 
                    name="teachingLevel"
                    value={formData.teachingLevel}
                    onChange={handleChange}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-sand-300 rounded-lg focus:outline-none focus:border-coral-500 text-sm sm:text-base"
                  >
                    <option value="">Select level</option>
                    <option value="Elementary (K-5)">Elementary (K-5)</option>
                    <option value="Middle School (6-8)">Middle School (6-8)</option>
                    <option value="High School (9-12)">High School (9-12)</option>
                    <option value="College/University">College/University</option>
                    <option value="Adult Education">Adult Education</option>
                    <option value="Multiple Levels">Multiple Levels</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-ink-700 font-bold mb-2 text-sm sm:text-base">OVERALL RATING</label>
                <select 
                  name="overallRating"
                  value={formData.overallRating}
                  onChange={handleChange}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-sand-300 rounded-lg focus:outline-none focus:border-coral-500 text-sm sm:text-base"
                >
                  <option value="5">⭐⭐⭐⭐⭐ - Excellent</option>
                  <option value="4">⭐⭐⭐⭐ - Good</option>
                  <option value="3">⭐⭐⭐ - Average</option>
                  <option value="2">⭐⭐ - Poor</option>
                  <option value="1">⭐ - Very Poor</option>
                </select>
              </div>
              
              <div>
                <label className="block text-ink-700 font-bold mb-2 text-sm sm:text-base">MOST USEFUL FEATURE</label>
                <input 
                  type="text" 
                  name="mostUsefulFeature"
                  value={formData.mostUsefulFeature}
                  onChange={handleChange}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-sand-300 rounded-lg focus:outline-none focus:border-coral-500 text-sm sm:text-base" 
                  placeholder="What feature do you find most helpful?" 
                />
              </div>
              
              <div>
                <label className="block text-ink-700 font-bold mb-2 text-sm sm:text-base">LEAST USEFUL FEATURE</label>
                <input 
                  type="text" 
                  name="leastUsefulFeature"
                  value={formData.leastUsefulFeature}
                  onChange={handleChange}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-sand-300 rounded-lg focus:outline-none focus:border-coral-500 text-sm sm:text-base" 
                  placeholder="What feature could be improved or removed?" 
                />
              </div>
              
              <div>
                <label className="block text-ink-700 font-bold mb-2 text-sm sm:text-base">IMPROVEMENT SUGGESTION</label>
                <textarea 
                  rows="3" 
                  name="improvementSuggestion"
                  value={formData.improvementSuggestion}
                  onChange={handleChange}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-sand-300 rounded-lg focus:outline-none focus:border-coral-500 text-sm sm:text-base" 
                  placeholder="What's one thing that would make TeAIch more useful for you?"
                ></textarea>
              </div>
              
              <div>
                <label className="block text-ink-700 font-bold mb-2 text-sm sm:text-base">ADDITIONAL COMMENTS</label>
                <textarea 
                  rows="4" 
                  name="additionalComments"
                  value={formData.additionalComments}
                  onChange={handleChange}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-sand-300 rounded-lg focus:outline-none focus:border-coral-500 text-sm sm:text-base" 
                  placeholder="Anything else you'd like us to know?"
                ></textarea>
              </div>
              
              <div className="text-center">
                <button 
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="bg-coral-300 text-white px-6 sm:px-8 py-2 sm:py-3 rounded-lg font-bold text-base sm:text-lg hover:bg-coral-500 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
                </button>
              </div>
            </div>
          </div>

          {/* Privacy Note */}
          <div className="bg-white rounded-xl sm:rounded-2xl border-4 border-ink-900 p-4 sm:p-6 md:p-8 text-center">
            <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-ink-900 mb-3 sm:mb-4">PRIVACY NOTE</h3>
            <p className="text-ink-600 text-sm sm:text-base md:text-lg">
              Your specific responses are kept confidential and used only to improve our platform.
            </p>
          </div>

        </div>
      </div>

      {/* Footer */}
      <footer className="bg-sand-100 border-t-4 border-coral-300 py-3 sm:py-4 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div onClick={() => navigate('/')} className="cursor-pointer hover:opacity-80 transition-opacity">
            <img src="/images/craidle-logo.png" alt="Craidle Intelligence Group" className="h-10 sm:h-14 md:h-16 w-auto" />
          </div>
          <div className="flex space-x-4 sm:space-x-6">
            <button 
              onClick={() => navigate('/feedback')}
              className="text-ink-900 hover:underline text-xs sm:text-sm font-semibold"
            >
              Feedback
            </button>
            <button 
              onClick={() => navigate('/legal/privacy-policy')}
              className="text-ink-900 hover:underline text-xs sm:text-sm font-semibold"
            >
              Privacy Policy
            </button>
            <button 
              onClick={() => navigate('/legal/terms-of-service')}
              className="text-ink-900 hover:underline text-xs sm:text-sm font-semibold"
            >
              Terms of Service
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FeedbackPage;